// src/tabs/HomeTab.jsx
import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Button,
  Divider,
  Skeleton,
  IconButton,
  Chip,
} from "@mui/material";

import VerifiedIcon from "@mui/icons-material/Verified";
import StoreIcon from "@mui/icons-material/Store";
import EventIcon from "@mui/icons-material/Event";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddIcon from "@mui/icons-material/Add";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import QrCodeIcon from "@mui/icons-material/QrCode";

import { formatDate } from "../../../utils/helpers";
import { useOutletContext } from "react-router-dom";

/* ─────────────────────────────────────────────
   Keyframe animations injected once via <style>
───────────────────────────────────────────── */
const ANIMATION_STYLES = `
  @keyframes floatBlob {
    0%   { transform: translate(0px, 0px) scale(1); }
    33%  { transform: translate(30px, -30px) scale(1.05); }
    66%  { transform: translate(-20px, 20px) scale(0.97); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  @keyframes floatBlob2 {
    0%   { transform: translate(0px, 0px) scale(1); }
    33%  { transform: translate(-25px, 25px) scale(1.04); }
    66%  { transform: translate(20px, -15px) scale(0.98); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1;   transform: scale(1); }
    50%       { opacity: 0.7; transform: scale(1.18); }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ─────────────────────────────────────────────
   Floating gradient blob helper
───────────────────────────────────────────── */
function GradientBlob({ sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed", // WebView Fix: Ensure this doesn't break document flow
        borderRadius: "50%",
        filter: "blur(72px)",
        pointerEvents: "none",
        zIndex: 0,
        ...sx,
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function HomeTab() {
  const {
    bootLoading,
    devices = [],
    onRaiseClaim,
    onViewPurchase,
  } = useOutletContext();

  /* ── Business logic — UNCHANGED ── */
  const devicesCount = devices.length;

  const claimsCount = useMemo(
    () => devices.reduce((sum, d) => sum + Number(d.available_claim || 0), 0),
    [devices]
  );

  const latestDevice = useMemo(() => {
    if (!devices.length) return null;
    return [...devices].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )[0];
  }, [devices]);

  const uniqueBrandCount = useMemo(
    () => new Set(devices.map((d) => d.brand_name).filter(Boolean)).size,
    [devices]
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw", // Strictly constrain to viewport
        overflowX: "hidden", // CRITICAL: Stops gradient blobs from zooming out the WebView
        boxSizing: "border-box",
        position: "relative",
        px: 2, // Native mobile horizontal padding
        pt: 2, // Top spacing
        pb: 10, // Bottom padding so the 'Raise a Claim' button clears the bottom tab bar
      }}
    >
      {/* ── Inject keyframes once ── */}
      <style>{ANIMATION_STYLES}</style>

      {/* ── Animated background blobs ── */}
      <GradientBlob
        sx={{
          width: 340,
          height: 340,
          top: -80,
          right: -100,
          background: "radial-gradient(circle, rgba(34,197,94,0.18) 0%, rgba(16,185,129,0.08) 70%)",
          animation: "floatBlob 18s ease-in-out infinite",
        }}
      />
      <GradientBlob
        sx={{
          width: 280,
          height: 280,
          bottom: 60,
          left: -90,
          background: "radial-gradient(circle, rgba(59,130,246,0.16) 0%, rgba(99,102,241,0.07) 70%)",
          animation: "floatBlob2 22s ease-in-out infinite",
        }}
      />
      <GradientBlob
        sx={{
          width: 200,
          height: 200,
          top: "45%",
          left: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.10) 0%, transparent 70%)",
          animation: "floatBlob 28s ease-in-out infinite reverse",
        }}
      />

      {/* ── Content ── */}
      <Stack
        spacing={2.5}
        sx={{
          position: "relative",
          zIndex: 1,
          animation: "fadeSlideUp 0.45s ease both",
          width: "100%", // Ensure the stack doesn't overflow
        }}
      >

        {/* ══════════════════════════════
            WARRANTY STATUS CARD
        ══════════════════════════════ */}
        <Card
          sx={(theme) => ({
            borderRadius: 0.2,
            background: `linear-gradient(135deg,
              #16a34a 0%,
              #0d9488 55%,
              #0369a1 100%)`,
            boxShadow: "0 8px 32px rgba(22,163,74,0.28), 0 2px 8px rgba(0,0,0,0.10)",
            border: "none",
            overflow: "hidden",
            position: "relative",
          })}
        >
          {/* Subtle inner shine overlay */}
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, transparent 60%)",
              pointerEvents: "none",
            }}
          />

          <CardContent sx={{ position: "relative", pb: "16px !important" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">

              {/* Left — label + status */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 1.2,
                    color: "rgba(255,255,255,0.75)",
                    textTransform: "uppercase",
                    mb: 0.4,
                  }}
                >
                  Warranty Status
                </Typography>

                {bootLoading ? (
                  <Skeleton
                    width={130}
                    height={38}
                    sx={{ bgcolor: "rgba(255,255,255,0.15)", borderRadius: 0.2 }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.15,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    Active
                  </Typography>
                )}

                <Stack direction="row" spacing={0.8} alignItems="center" mt={0.8}>
                  <VerifiedIcon
                    fontSize="small"
                    sx={{
                      color: "#86efac",
                      animation: "pulse 2.4s ease-in-out infinite",
                    }}
                  />
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
                    {devicesCount ? "Your devices are protected" : "No active devices"}
                  </Typography>
                </Stack>
              </Box>

              {/* Right — summary chip */}
              <Chip
                label={`${devicesCount} Device${devicesCount !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 11,
                  border: "1px solid rgba(255,255,255,0.25)",
                  backdropFilter: "blur(6px)",
                  borderRadius: 0.2,
                  mt: 0.5,
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ══════════════════════════════
            KPI CARDS
        ══════════════════════════════ */}
        <Stack direction="row" spacing={1.5}>

          {/* Devices KPI */}
          <Card
            sx={{
              flex: 1,
              borderRadius: 0.2,
              border: "1px solid #e8f0fe",
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 1.8, px: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.2} mb={0.8}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 0.2,
                    bgcolor: "#f0fdf4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PhoneAndroidIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                </Box>
                <Typography
                  sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}
                >
                  Devices
                </Typography>
              </Stack>

              {bootLoading ? (
                <Skeleton width={44} height={32} sx={{ borderRadius: 0.2 }} />
              ) : (
                <Typography
                  sx={{ fontSize: 24, fontWeight: 800, color: "#111827", lineHeight: 1 }}
                >
                  {devicesCount}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Claims Balance KPI */}
          <Card
            sx={{
              flex: 1,
              borderRadius: 0.2,
              border: "1px solid #e8f0fe",
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 1.8, px: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.2} mb={0.8}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 0.2,
                    bgcolor: "#eff6ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AssignmentTurnedInIcon sx={{ fontSize: 16, color: "#2563eb" }} />
                </Box>
                <Typography
                  sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}
                >
                  Claims Balance
                </Typography>
              </Stack>

              {bootLoading ? (
                <Skeleton width={44} height={32} sx={{ borderRadius: 0.2 }} />
              ) : (
                <Typography
                  sx={{ fontSize: 24, fontWeight: 800, color: "#111827", lineHeight: 1 }}
                >
                  {claimsCount}
                </Typography>
              )}
            </CardContent>
          </Card>

        </Stack>

        {/* ══════════════════════════════
            MY PURCHASES CARD
        ══════════════════════════════ */}
        <Card
          sx={{
            borderRadius: 0.2,
            border: "1px solid #e8f0fe",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            background: "#fff",
          }}
        >
          <CardContent sx={{ pb: "16px !important" }}>

            {/* Card header */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: "#f0fdf4",
                  color: "#16a34a",
                  width: 40,
                  height: 40,
                  borderRadius: 0.2,
                  border: "1px solid #dcfce7",
                }}
              >
                <StoreIcon fontSize="small" />
              </Avatar>

              <Box flex={1}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
                  My Purchases
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.1 }}>
                  {devicesCount} device{devicesCount !== 1 ? "s" : ""} &nbsp;·&nbsp; {uniqueBrandCount} brand{uniqueBrandCount !== 1 ? "s" : ""}
                </Typography>
              </Box>

              <IconButton
                onClick={onViewPurchase}
                size="small"
                sx={{
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 0.2,
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <ListAltIcon fontSize="small" sx={{ color: "#475569" }} />
              </IconButton>
            </Stack>

            <Divider sx={{ my: 1.8, borderColor: "#f1f5f9" }} />

            {/* Latest device details */}
            {latestDevice ? (
              <Stack spacing={1.2}>

                {/* Device name + brand/category */}
                <Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 14, color: "#111827", mb: 0.2 }}
                  >
                    {latestDevice.product_name || "Device"}
                  </Typography>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Chip
                      label={latestDevice.brand_name || "—"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 600,
                        bgcolor: "#f0fdf4",
                        color: "#15803d",
                        border: "1px solid #bbf7d0",
                        borderRadius: 0.2,
                      }}
                    />
                    {latestDevice.category_name && (
                      <Chip
                        label={latestDevice.category_name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 500,
                          bgcolor: "#f8fafc",
                          color: "#64748b",
                          border: "1px solid #e2e8f0",
                          borderRadius: 0.2,
                        }}
                      />
                    )}
                  </Stack>
                </Box>

                {/* Expiry */}
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <EventIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                  <Typography sx={{ fontSize: 12, color: "#475569" }}>
                    Expiry:{" "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: "#111827" }}
                    >
                      {formatDate(latestDevice.expiry_date)}
                    </Box>
                  </Typography>
                </Stack>

                {/* IMEI + Serial — two-column grid */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    bgcolor: "#f8fafc",
                    border: "1px solid #f1f5f9",
                    borderRadius: 0.2,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Stack direction="row" spacing={0.7} alignItems="center" flex={1}>
                    <FingerprintIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                    <Box>
                      <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
                        IMEI
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151", fontFamily: "monospace" }}>
                        {latestDevice.imei1 || "—"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0" }} />

                  <Stack direction="row" spacing={0.7} alignItems="center" flex={1}>
                    <QrCodeIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                    <Box>
                      <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
                        Serial
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151", fontFamily: "monospace" }}>
                        {latestDevice.serial || "—"}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

              </Stack>
            ) : (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                No devices found.
              </Typography>
            )}

          </CardContent>
        </Card>

        {/* ══════════════════════════════
            CTA BUTTON
        ══════════════════════════════ */}
        <Button
          size="large"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onRaiseClaim(null)}
          sx={{
            borderRadius: 0.2,
            py: 1.4,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 0.2,
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            boxShadow: "0 4px 14px rgba(22,163,74,0.35)",
            border: "none",
            "&:hover": {
              background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
              boxShadow: "0 6px 18px rgba(22,163,74,0.42)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0px)",
              boxShadow: "0 2px 8px rgba(22,163,74,0.28)",
            },
            transition: "all 0.18s ease",
          }}
        >
          Raise a Claim
        </Button>

      </Stack>
    </Box>
  );
}
//***********************************************working code chetan******************************************************** */

// // src/tabs/HomeTab.jsx
// import React, { useMemo } from "react";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Stack,
//   Avatar,
//   Button,
//   Divider,
//   Skeleton,
//   IconButton,
//   Chip,
// } from "@mui/material";

// import VerifiedIcon from "@mui/icons-material/Verified";
// import StoreIcon from "@mui/icons-material/Store";
// import EventIcon from "@mui/icons-material/Event";
// import ListAltIcon from "@mui/icons-material/ListAlt";
// import AddIcon from "@mui/icons-material/Add";
// import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
// import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
// import FingerprintIcon from "@mui/icons-material/Fingerprint";
// import QrCodeIcon from "@mui/icons-material/QrCode";

// import { formatDate } from "../../../utils/helpers";
// import { useOutletContext } from "react-router-dom";

// /* ─────────────────────────────────────────────
//    Keyframe animations injected once via <style>
// ───────────────────────────────────────────── */
// const ANIMATION_STYLES = `
//   @keyframes floatBlob {
//     0%   { transform: translate(0px, 0px) scale(1); }
//     33%  { transform: translate(30px, -30px) scale(1.05); }
//     66%  { transform: translate(-20px, 20px) scale(0.97); }
//     100% { transform: translate(0px, 0px) scale(1); }
//   }
//   @keyframes floatBlob2 {
//     0%   { transform: translate(0px, 0px) scale(1); }
//     33%  { transform: translate(-25px, 25px) scale(1.04); }
//     66%  { transform: translate(20px, -15px) scale(0.98); }
//     100% { transform: translate(0px, 0px) scale(1); }
//   }
//   @keyframes pulse {
//     0%, 100% { opacity: 1;   transform: scale(1); }
//     50%       { opacity: 0.7; transform: scale(1.18); }
//   }
//   @keyframes fadeSlideUp {
//     from { opacity: 0; transform: translateY(14px); }
//     to   { opacity: 1; transform: translateY(0); }
//   }
// `;

// /* ─────────────────────────────────────────────
//    Floating gradient blob helper
// ───────────────────────────────────────────── */
// function GradientBlob({ sx }) {
//   return (
//     <Box
//       aria-hidden="true"
//       sx={{
//         position: "fixed",
//         borderRadius: "50%",
//         filter: "blur(72px)",
//         pointerEvents: "none",
//         zIndex: 0,
//         ...sx,
//       }}
//     />
//   );
// }

// /* ─────────────────────────────────────────────
//    Main component
// ───────────────────────────────────────────── */
// export default function HomeTab() {
//   const {
//     bootLoading,
//     devices = [],
//     onRaiseClaim,
//     onViewPurchase,
//   } = useOutletContext();

//   /* ── Business logic — UNCHANGED ── */
//   const devicesCount = devices.length;

//   const claimsCount = useMemo(
//     () => devices.reduce((sum, d) => sum + Number(d.available_claim || 0), 0),
//     [devices]
//   );

//   const latestDevice = useMemo(() => {
//     if (!devices.length) return null;
//     return [...devices].sort(
//       (a, b) => new Date(b.created_at) - new Date(a.created_at)
//     )[0];
//   }, [devices]);

//   const uniqueBrandCount = useMemo(
//     () => new Set(devices.map((d) => d.brand_name).filter(Boolean)).size,
//     [devices]
//   );

//   return (
//     <>
//       {/* ── Inject keyframes once ── */}
//       <style>{ANIMATION_STYLES}</style>

//       {/* ── Animated background blobs ── */}
//       <GradientBlob
//         sx={{
//           width: 340,
//           height: 340,
//           top: -80,
//           right: -100,
//           background: "radial-gradient(circle, rgba(34,197,94,0.18) 0%, rgba(16,185,129,0.08) 70%)",
//           animation: "floatBlob 18s ease-in-out infinite",
//         }}
//       />
//       <GradientBlob
//         sx={{
//           width: 280,
//           height: 280,
//           bottom: 60,
//           left: -90,
//           background: "radial-gradient(circle, rgba(59,130,246,0.16) 0%, rgba(99,102,241,0.07) 70%)",
//           animation: "floatBlob2 22s ease-in-out infinite",
//         }}
//       />
//       <GradientBlob
//         sx={{
//           width: 200,
//           height: 200,
//           top: "45%",
//           left: "50%",
//           background: "radial-gradient(circle, rgba(16,185,129,0.10) 0%, transparent 70%)",
//           animation: "floatBlob 28s ease-in-out infinite reverse",
//         }}
//       />

//       {/* ── Content ── */}
//       <Stack
//         spacing={2.5}
//         sx={{
//           position: "relative",
//           zIndex: 1,
//           animation: "fadeSlideUp 0.45s ease both",
//         }}
//       >

//         {/* ══════════════════════════════
//             WARRANTY STATUS CARD
//         ══════════════════════════════ */}
//         <Card
//           sx={(theme) => ({
//             borderRadius: 0.2,
//             background: `linear-gradient(135deg,
//               #16a34a 0%,
//               #0d9488 55%,
//               #0369a1 100%)`,
//             boxShadow: "0 8px 32px rgba(22,163,74,0.28), 0 2px 8px rgba(0,0,0,0.10)",
//             border: "none",
//             overflow: "hidden",
//             position: "relative",
//           })}
//         >
//           {/* Subtle inner shine overlay */}
//           <Box
//             aria-hidden="true"
//             sx={{
//               position: "absolute",
//               inset: 0,
//               background:
//                 "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, transparent 60%)",
//               pointerEvents: "none",
//             }}
//           />

//           <CardContent sx={{ position: "relative", pb: "16px !important" }}>
//             <Stack direction="row" justifyContent="space-between" alignItems="flex-start">

//               {/* Left — label + status */}
//               <Box>
//                 <Typography
//                   sx={{
//                     fontSize: 11,
//                     fontWeight: 600,
//                     letterSpacing: 1.2,
//                     color: "rgba(255,255,255,0.75)",
//                     textTransform: "uppercase",
//                     mb: 0.4,
//                   }}
//                 >
//                   Warranty Status
//                 </Typography>

//                 {bootLoading ? (
//                   <Skeleton
//                     width={130}
//                     height={38}
//                     sx={{ bgcolor: "rgba(255,255,255,0.15)", borderRadius: 0.2 }}
//                   />
//                 ) : (
//                   <Typography
//                     sx={{
//                       fontSize: 28,
//                       fontWeight: 800,
//                       color: "#fff",
//                       lineHeight: 1.15,
//                       letterSpacing: "-0.5px",
//                     }}
//                   >
//                     Active
//                   </Typography>
//                 )}

//                 <Stack direction="row" spacing={0.8} alignItems="center" mt={0.8}>
//                   <VerifiedIcon
//                     fontSize="small"
//                     sx={{
//                       color: "#86efac",
//                       animation: "pulse 2.4s ease-in-out infinite",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
//                     {devicesCount ? "Your devices are protected" : "No active devices"}
//                   </Typography>
//                 </Stack>
//               </Box>

//               {/* Right — summary chip */}
//               <Chip
//                 label={`${devicesCount} Device${devicesCount !== 1 ? "s" : ""}`}
//                 size="small"
//                 sx={{
//                   bgcolor: "rgba(255,255,255,0.15)",
//                   color: "#fff",
//                   fontWeight: 600,
//                   fontSize: 11,
//                   border: "1px solid rgba(255,255,255,0.25)",
//                   backdropFilter: "blur(6px)",
//                   borderRadius: 0.2,
//                   mt: 0.5,
//                 }}
//               />
//             </Stack>
//           </CardContent>
//         </Card>

//         {/* ══════════════════════════════
//             KPI CARDS
//         ══════════════════════════════ */}
//         <Stack direction="row" spacing={1.5}>

//           {/* Devices KPI */}
//           <Card
//             sx={{
//               flex: 1,
//               borderRadius: 0.2,
//               border: "1px solid #e8f0fe",
//               boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
//               background: "#fff",
//             }}
//           >
//             <CardContent sx={{ py: 1.8, px: 2 }}>
//               <Stack direction="row" alignItems="center" spacing={1.2} mb={0.8}>
//                 <Box
//                   sx={{
//                     width: 30,
//                     height: 30,
//                     borderRadius: 0.2,
//                     bgcolor: "#f0fdf4",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                   }}
//                 >
//                   <PhoneAndroidIcon sx={{ fontSize: 16, color: "#16a34a" }} />
//                 </Box>
//                 <Typography
//                   sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}
//                 >
//                   Devices
//                 </Typography>
//               </Stack>

//               {bootLoading ? (
//                 <Skeleton width={44} height={32} sx={{ borderRadius: 0.2 }} />
//               ) : (
//                 <Typography
//                   sx={{ fontSize: 24, fontWeight: 800, color: "#111827", lineHeight: 1 }}
//                 >
//                   {devicesCount}
//                 </Typography>
//               )}
//             </CardContent>
//           </Card>

//           {/* Claims Balance KPI */}
//           <Card
//             sx={{
//               flex: 1,
//               borderRadius: 0.2,
//               border: "1px solid #e8f0fe",
//               boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
//               background: "#fff",
//             }}
//           >
//             <CardContent sx={{ py: 1.8, px: 2 }}>
//               <Stack direction="row" alignItems="center" spacing={1.2} mb={0.8}>
//                 <Box
//                   sx={{
//                     width: 30,
//                     height: 30,
//                     borderRadius: 0.2,
//                     bgcolor: "#eff6ff",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                   }}
//                 >
//                   <AssignmentTurnedInIcon sx={{ fontSize: 16, color: "#2563eb" }} />
//                 </Box>
//                 <Typography
//                   sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}
//                 >
//                   Claims Balance
//                 </Typography>
//               </Stack>

//               {bootLoading ? (
//                 <Skeleton width={44} height={32} sx={{ borderRadius: 0.2 }} />
//               ) : (
//                 <Typography
//                   sx={{ fontSize: 24, fontWeight: 800, color: "#111827", lineHeight: 1 }}
//                 >
//                   {claimsCount}
//                 </Typography>
//               )}
//             </CardContent>
//           </Card>

//         </Stack>

//         {/* ══════════════════════════════
//             MY PURCHASES CARD
//         ══════════════════════════════ */}
//         <Card
//           sx={{
//             borderRadius: 0.2,
//             border: "1px solid #e8f0fe",
//             boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
//             background: "#fff",
//           }}
//         >
//           <CardContent sx={{ pb: "16px !important" }}>

//             {/* Card header */}
//             <Stack direction="row" spacing={1.5} alignItems="center">
//               <Avatar
//                 sx={{
//                   bgcolor: "#f0fdf4",
//                   color: "#16a34a",
//                   width: 40,
//                   height: 40,
//                   borderRadius: 0.2,
//                   border: "1px solid #dcfce7",
//                 }}
//               >
//                 <StoreIcon fontSize="small" />
//               </Avatar>

//               <Box flex={1}>
//                 <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
//                   My Purchases
//                 </Typography>
//                 <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.1 }}>
//                   {devicesCount} device{devicesCount !== 1 ? "s" : ""} &nbsp;·&nbsp; {uniqueBrandCount} brand{uniqueBrandCount !== 1 ? "s" : ""}
//                 </Typography>
//               </Box>

//               <IconButton
//                 onClick={onViewPurchase}
//                 size="small"
//                 sx={{
//                   bgcolor: "#f8fafc",
//                   border: "1px solid #e2e8f0",
//                   borderRadius: 0.2,
//                   "&:hover": { bgcolor: "#f1f5f9" },
//                 }}
//               >
//                 <ListAltIcon fontSize="small" sx={{ color: "#475569" }} />
//               </IconButton>
//             </Stack>

//             <Divider sx={{ my: 1.8, borderColor: "#f1f5f9" }} />

//             {/* Latest device details */}
//             {latestDevice ? (
//               <Stack spacing={1.2}>

//                 {/* Device name + brand/category */}
//                 <Box>
//                   <Typography
//                     sx={{ fontWeight: 700, fontSize: 14, color: "#111827", mb: 0.2 }}
//                   >
//                     {latestDevice.product_name || "Device"}
//                   </Typography>
//                   <Stack direction="row" spacing={0.8} alignItems="center">
//                     <Chip
//                       label={latestDevice.brand_name || "—"}
//                       size="small"
//                       sx={{
//                         height: 20,
//                         fontSize: 10,
//                         fontWeight: 600,
//                         bgcolor: "#f0fdf4",
//                         color: "#15803d",
//                         border: "1px solid #bbf7d0",
//                         borderRadius: 0.2,
//                       }}
//                     />
//                     {latestDevice.category_name && (
//                       <Chip
//                         label={latestDevice.category_name}
//                         size="small"
//                         sx={{
//                           height: 20,
//                           fontSize: 10,
//                           fontWeight: 500,
//                           bgcolor: "#f8fafc",
//                           color: "#64748b",
//                           border: "1px solid #e2e8f0",
//                           borderRadius: 0.2,
//                         }}
//                       />
//                     )}
//                   </Stack>
//                 </Box>

//                 {/* Expiry */}
//                 <Stack direction="row" spacing={0.8} alignItems="center">
//                   <EventIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
//                   <Typography sx={{ fontSize: 12, color: "#475569" }}>
//                     Expiry:{" "}
//                     <Box
//                       component="span"
//                       sx={{ fontWeight: 700, color: "#111827" }}
//                     >
//                       {formatDate(latestDevice.expiry_date)}
//                     </Box>
//                   </Typography>
//                 </Stack>

//                 {/* IMEI + Serial — two-column grid */}
//                 <Stack
//                   direction="row"
//                   spacing={1.5}
//                   sx={{
//                     bgcolor: "#f8fafc",
//                     border: "1px solid #f1f5f9",
//                     borderRadius: 0.2,
//                     px: 1.5,
//                     py: 1,
//                   }}
//                 >
//                   <Stack direction="row" spacing={0.7} alignItems="center" flex={1}>
//                     <FingerprintIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
//                     <Box>
//                       <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
//                         IMEI
//                       </Typography>
//                       <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151", fontFamily: "monospace" }}>
//                         {latestDevice.imei1 || "—"}
//                       </Typography>
//                     </Box>
//                   </Stack>

//                   <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0" }} />

//                   <Stack direction="row" spacing={0.7} alignItems="center" flex={1}>
//                     <QrCodeIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
//                     <Box>
//                       <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
//                         Serial
//                       </Typography>
//                       <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151", fontFamily: "monospace" }}>
//                         {latestDevice.serial || "—"}
//                       </Typography>
//                     </Box>
//                   </Stack>
//                 </Stack>

//               </Stack>
//             ) : (
//               <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
//                 No devices found.
//               </Typography>
//             )}

//           </CardContent>
//         </Card>

//         {/* ══════════════════════════════
//             CTA BUTTON
//         ══════════════════════════════ */}
//         <Button
//           size="large"
//           variant="contained"
//           startIcon={<AddIcon />}
//           onClick={() => onRaiseClaim(null)}
//           sx={{
//             borderRadius: 0.2,
//             py: 1.4,
//             textTransform: "none",
//             fontWeight: 700,
//             fontSize: 14,
//             letterSpacing: 0.2,
//             background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
//             boxShadow: "0 4px 14px rgba(22,163,74,0.35)",
//             border: "none",
//             "&:hover": {
//               background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
//               boxShadow: "0 6px 18px rgba(22,163,74,0.42)",
//               transform: "translateY(-1px)",
//             },
//             "&:active": {
//               transform: "translateY(0px)",
//               boxShadow: "0 2px 8px rgba(22,163,74,0.28)",
//             },
//             transition: "all 0.18s ease",
//           }}
//         >
//           Raise a Claim
//         </Button>

//       </Stack>
//     </>
//   );
// }

//**********************************************************working code chetan************************************************************* */

// // src/tabs/HomeTab.jsx
// import React, { useMemo, useState } from "react";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Stack,
//   Avatar,
//   Button,
//   Divider,
//   Skeleton,
//   IconButton,
// } from "@mui/material";

// import VerifiedIcon from "@mui/icons-material/Verified";
// import StoreIcon from "@mui/icons-material/Store";
// import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
// import EventIcon from "@mui/icons-material/Event";
// import ListAltIcon from "@mui/icons-material/ListAlt";
// import AddIcon from "@mui/icons-material/Add";
// import { formatDate } from "../../../utils/helpers";
// import { useOutletContext } from "react-router-dom";

// /**
//  * HomeTab
//  * - KPI summary
//  * - Latest purchase
//  * - Multi-store awareness
//  * - Raise claim CTA
//  */
// export default function HomeTab() {
//   const {
//     bootLoading,
//     devices,
//     onRaiseClaim,
//     onOpenPurchases,
//     onViewPurchase,
//   } = useOutletContext();

//   const [claimStep, setClaimStep] = useState(0);
//   const [openClaim, setOpenClaim] = useState(false);
//   const [claimDraft, setClaimDraft] = useState({
//     device_id: "",
//     issues: [],
//     notes: "",
//     photos: [],
//     service: "pickup",
//     pickup_address_id: "",
//     drop_pincode: "",
//     drop_partner_id: "",
//   });
//   /** 🔹 MOCK DATA (replace with API later) */
//   const purchases = [
//     {
//       id: "pur-1",
//       model_name: "Google Pixel 8",
//       purchase_date: "2025-12-20",
//       order_no: "ORD-77881",
//       serial_no: "SN-PXL8-88991234",
//       imei: "356789101112131",
//       warranty_expiry: "2026-12-20",
//       purchased_from: {
//         name: "Akash Mobile World",
//       },
//     },
//     {
//       id: "pur-2",
//       model_name: "Samsung Galaxy S24",
//       purchase_date: "2025-11-08",
//       order_no: "ORD-66420",
//       serial_no: "SN-S24-10012025",
//       imei: "357891011121314",
//       warranty_expiry: "2026-11-08",
//       purchased_from: {
//         name: "City Mobiles",
//       },
//     },
//   ];

//   const [addresses, setAddresses] = useState([
//     {
//       id: "local-1",
//       name: "Rohit Sharma",
//       mobile: "9876543210",
//       address1: "Flat 301, ABC Apartment",
//       address2: "Near City Mall",
//       city: "Pune",
//       state: "Maharashtra",
//       pincode: "411001",
//       lat: 18.5204,
//       lng: 73.8567,
//     },
//   ]);

//   const devicesCount = 2;
//   const claimsCount = 2;

//   /** 🔹 COMPUTED */
//   const purchasesSorted = useMemo(() => {
//     return [...purchases].sort(
//       (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
//     );
//   }, []);

//   const latestPurchase = purchasesSorted[0];
//   const uniqueShopCount = new Set(
//     purchases.map((p) => p.purchased_from?.name?.toLowerCase())
//   ).size;

//   const startClaim = (preselectedDeviceId) => {
//     const firstAddressId = addresses?.[0]?.id || "";
//     setClaimDraft({
//       device_id: preselectedDeviceId || "",
//       issues: [],
//       notes: "",
//       photos: [],
//       service: "pickup",
//       pickup_address_id: firstAddressId,
//       drop_pincode: "",
//       drop_partner_id: "",
//     });
//     setClaimStep(preselectedDeviceId ? 1 : 0);
//     setOpenClaim(true);
//   };

//   return (
//     <Stack spacing={3}>
//       {/* ================= WARRANTY STATUS ================= */}
//       <Card
//         sx={{
//           borderRadius: 4,
//           p: 1,
//           color: "#fff",
//           background: "linear-gradient(135deg,#4f46e5,#6366f1)",
//         }}
//       >
//         <CardContent>
//           <Typography variant="body2">Warranty Status</Typography>

//           {bootLoading ? (
//             <Skeleton variant="text" width={160} height={44} />
//           ) : (
//             <Typography variant="h4" fontWeight={800}>
//               Active
//             </Typography>
//           )}

//           <Stack direction="row" spacing={1} alignItems="center">
//             <VerifiedIcon fontSize="small" />
//             <Typography variant="body2">
//               All devices protected
//             </Typography>
//           </Stack>
//         </CardContent>
//       </Card>

//       {/* ================= KPI CARDS ================= */}
//       <Stack direction="row" spacing={2}>
//         <Card
//           sx={{
//             flex: 1,
//             borderRadius: 3,
//             cursor: "pointer",
//             "&:hover": { transform: "translateY(-1px)" },
//           }}
//         >
//           <CardContent>
//             <Typography variant="caption">Devices</Typography>
//             {bootLoading ? (
//               <Skeleton width={60} height={36} />
//             ) : (
//               <Typography variant="h5" fontWeight={800}>
//                 {devicesCount}
//               </Typography>
//             )}
//           </CardContent>
//         </Card>

//         <Card
//           sx={{
//             flex: 1,
//             borderRadius: 3,
//             cursor: "pointer",
//             "&:hover": { transform: "translateY(-1px)" },
//           }}
//         >
//           <CardContent>
//             <Typography variant="caption">Claims</Typography>
//             {bootLoading ? (
//               <Skeleton width={60} height={36} />
//             ) : (
//               <Typography variant="h5" fontWeight={800}>
//                 {claimsCount}
//               </Typography>
//             )}
//           </CardContent>
//         </Card>
//       </Stack>

//       {/* ================= PURCHASE SUMMARY ================= */}
//       <Card
//         sx={{
//           borderRadius: 4,
//           cursor: "pointer",
//           "&:hover": { transform: "translateY(-1px)" },
//         }}
//       >
//         <CardContent>
//           <Stack direction="row" spacing={1.5} alignItems="center">
//             <Avatar sx={{ bgcolor: "#eef2ff" }}>
//               <StoreIcon color="primary"  />
//             </Avatar>

//             <Box flex={1}>
//               <Typography fontWeight={500}>
//                 Where Purchased
//               </Typography>
//               <Typography variant="body2" color="text.secondary">
//                 {purchases.length} purchase
//                 {purchases.length !== 1 ? "s" : ""} •{" "}
//                 {uniqueShopCount} shop
//                 {uniqueShopCount !== 1 ? "s" : ""}
//               </Typography>
//             </Box>

//             <IconButton>
//               <ListAltIcon onClick={onViewPurchase} />
//             </IconButton>
//           </Stack>

//           <Divider sx={{ my: 1.5 }} />

//           {latestPurchase ? (
//             <Stack spacing={1}>
//               <Typography fontWeight={500}>
//                 {latestPurchase.model_name}
//               </Typography>

//               <Typography variant="body2" color="text.secondary">
//                 {latestPurchase.purchased_from?.name} •{" "}
//                 {formatDate(latestPurchase.purchase_date)}
//               </Typography>

//               <Stack direction="row" spacing={1} alignItems="center">
//                 <ReceiptLongIcon fontSize="small" />
//                 <Typography variant="body2">
//                   Order No:{" "}
//                   <b>{latestPurchase.order_no}</b>
//                 </Typography>
//               </Stack>

//               <Typography variant="caption" color="text.secondary">
//                 S/N: {latestPurchase.serial_no}
//                 {latestPurchase.imei &&
//                   ` • IMEI: ${latestPurchase.imei}`}
//                 {latestPurchase.warranty_expiry &&
//                   ` • Expiry: ${formatDate(
//                     latestPurchase.warranty_expiry
//                   )}`}
//               </Typography>
//             </Stack>
//           ) : (
//             <Typography variant="body2" color="text.secondary">
//               No purchase found.
//             </Typography>
//           )}
//         </CardContent>
//       </Card>

//       {/* ================= CTA ================= */}
//       <Button
//         size="large"
//         variant="contained"
//         startIcon={<AddIcon />}
//       onClick={() => onRaiseClaim(null)}
//         sx={{ borderRadius: 3, py: 1.2 }}
//       >
//         Raise a Claim
//       </Button>

//     </Stack>
//   );
// }

// import React from "react";

// import { ShieldCheck, Store, List } from "lucide-react";
// import Card, { CardBody } from "../ui/Card";

// export default function HomeTab({
//   deviceCount,
//   claimCount,
//   purchases,
//   uniqueShopCount,
//   latestPurchase,
//   onGoDevices,
//   onGoClaims,
//   onOpenPurchases,
//   onStartClaim,
// }) {
//   return (
//     <div className="space-y-4">
//       <Card className="bg-gradient-to-br from-indigo-600 to-indigo-400 border-0 text-white">
//         <CardBody className="p-5">
//           <div className="text-sm opacity-90">Warranty Status</div>
//           <div className="text-3xl font-extrabold mt-1">Active</div>
//           <div className="flex items-center gap-2 mt-2 text-sm">
//             <ShieldCheck size={16} />
//             <span className="opacity-95">All devices protected</span>
//           </div>
//         </CardBody>
//       </Card>

//       <div className="grid grid-cols-2 gap-3">
//         <Card onClick={onGoDevices}>
//           <CardBody>
//             <div className="text-xs text-slate-500 font-semibold">Devices</div>
//             <div className="text-2xl font-extrabold text-slate-900 mt-1">{deviceCount}</div>
//           </CardBody>
//         </Card>
//         <Card onClick={onGoClaims}>
//           <CardBody>
//             <div className="text-xs text-slate-500 font-semibold">Claims</div>
//             <div className="text-2xl font-extrabold text-slate-900 mt-1">{claimCount}</div>
//           </CardBody>
//         </Card>
//       </div>

//       <Card onClick={() => onOpenPurchases?.(latestPurchase?.id)}>
//         <CardBody>
//           <div className="flex items-center gap-3">
//             <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center">
//               <Store className="text-indigo-600" size={18} />
//             </div>
//             <div className="flex-1">
//               <div className="font-extrabold text-slate-900">Where Purchased</div>
//               <div className="text-sm text-slate-500">
//                 {purchases.length} purchase{purchases.length === 1 ? "" : "s"} • {uniqueShopCount} shop
//                 {uniqueShopCount === 1 ? "" : "s"}
//               </div>
//             </div>
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 onOpenPurchases?.(latestPurchase?.id);
//               }}
//               className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50"
//             >
//               <List size={18} className="text-slate-700" />
//             </button>
//           </div>

//           <div className="h-px bg-slate-100 my-3" />

//           {latestPurchase ? (
//             <div className="space-y-1">
//               <div className="font-extrabold text-slate-900">{latestPurchase.model_name}</div>
//               <div className="text-sm text-slate-500">
//                 {latestPurchase.purchased_from?.name} • {latestPurchase.purchase_date}
//               </div>
//               <div className="text-xs text-slate-500">
//                 Order No: <b className="text-slate-700">{latestPurchase.order_no}</b>
//               </div>
//               <div className="text-xs text-slate-500">
//                 S/N: {latestPurchase.serial_no}
//                 {latestPurchase.imei ? ` • IMEI: ${latestPurchase.imei}` : ""}
//                 {latestPurchase.warranty_expiry ? ` • Expiry: ${latestPurchase.warranty_expiry}` : ""}
//               </div>
//             </div>
//           ) : (
//             <div className="text-sm text-slate-500">No purchase found.</div>
//           )}
//         </CardBody>
//       </Card>

//       <button
//         onClick={onStartClaim}
//         className="w-full rounded-2xl bg-indigo-600 text-white font-extrabold py-3.5 shadow hover:bg-indigo-700 transition"
//       >
//         Raise a Claim
//       </button>
//     </div>
//   );
// }